import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode
} from 'react';
import type { LoginInfo } from '../../__generated__/graphql';
import useEnvironment from './Environment';
import useLogger from './Logging';

export interface AuthStatusResponse {
  isAuthenticated: boolean;
  loginInfo: LoginInfo;
}

export const useSessionStatus = () => {
  const logger = useLogger();
  const { natoma } = useEnvironment();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginInfo, setLoginInfo] = useState<LoginInfo | undefined>(undefined);
  const [refreshSwitch, setRefreshSwitch] = useState(false);
  const [isSilentRefresh, setIsSilentRefresh] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchSessionData = async () => {
      if (!natoma.rootDomain) {
        return;
      }
      setFetching(true);
      try {
        const response = await fetch(
          `https://api.${natoma.rootDomain}/auth/status`,
          {
            credentials: 'include'
          }
        );
        if (!isMounted) return;
        try {
          const data: AuthStatusResponse = await response.json();
          if (!isMounted) return;
          setLoginInfo(data.loginInfo);
          setIsAuthenticated(!!data.isAuthenticated);
          setHasInitialized(true);
        } catch (error) {
          if (!isMounted) return;
          setIsAuthenticated(false);
        }
      } catch (error) {
        logger.error(`Error fetching session data: ${error}`);
        if (!isMounted) return;
        setIsAuthenticated(false);
      } finally {
        if (isMounted) {
          setFetching(false);
          setIsSilentRefresh(false);
        }
      }
    };
    fetchSessionData().catch((error) => {
      logger.error(`Unhandled session fetch error: ${error}`);
    });

    return () => {
      isMounted = false;
    };
  }, [natoma.rootDomain, refreshSwitch, logger]);

  const refreshSession = useCallback((options?: { silent?: boolean }) => {
    setIsSilentRefresh(options?.silent ?? false);
    setRefreshSwitch((prevSwitchValue) => !prevSwitchValue);
  }, []);

  return {
    loading: !hasInitialized || (isSilentRefresh ? false : fetching),
    isAuthenticated,
    loginInfo,
    refreshSession
  };
};

export type SessionContextValues = {
  loading: boolean;
  isAuthenticated: boolean;
  loginInfo: LoginInfo | undefined;
  refreshSession: (options?: { silent?: boolean }) => void;
};

export const SessionContext = createContext<SessionContextValues>({
  loading: true,
  isAuthenticated: false,
  loginInfo: undefined,
  refreshSession: () => {}
});

export const useSessionContext = () => {
  return useContext(SessionContext);
};

export function SessionProvider({ children }: { children: ReactNode }) {
  const { loading, isAuthenticated, loginInfo, refreshSession } =
    useSessionStatus();
  return (
    <SessionContext.Provider
      value={{ loading, isAuthenticated, loginInfo, refreshSession }}
    >
      {children}
    </SessionContext.Provider>
  );
}
